import { axiosInstance } from './api';
import type { AdminTunnelCapability } from './adminTunnel';

export interface RouterRecord {
  zone_id: number;
  model_name: string;
  gateway_ip: string | null;
  /** External/WAN IP from the discovery probe — operator-editable. */
  wan_ip?: string | null;
  admin_url: string;
  remote_access_url: string;
  username: string;
  password: string;
  notes: string;
  /** Probe-populated display chips for the Router card. All optional —
   *  the columns are populated by the autodiscovery primitive and are
   *  left null when no probe has run yet. */
  model?: string | null;
  firmware_version?: string | null;
  factory_firmware_version?: string | null;
  bridge_layout?: unknown;
  admin_tunnel_capability?: AdminTunnelCapability | null;
  created_at: string;
  updated_at: string;
}

export type RouterUpsert = Partial<
  Pick<
    RouterRecord,
    | 'model_name'
    | 'gateway_ip'
    | 'wan_ip'
    | 'admin_url'
    | 'remote_access_url'
    | 'username'
    | 'password'
    | 'notes'
  >
>;

const url = (zoneId: number | string) => `/equipment_discovery/zones/${zoneId}/router/`;

export const getZoneRouter = (zoneId: number | string) =>
  axiosInstance.get<RouterRecord>(url(zoneId));

export const upsertZoneRouter = (zoneId: number | string, payload: RouterUpsert) =>
  axiosInstance.put<RouterRecord>(url(zoneId), payload);

export const deleteZoneRouter = (zoneId: number | string) =>
  axiosInstance.delete<void>(url(zoneId));

// ---- Router probe (autodiscovery Phase A) -------------------------------
//
// Read-only RCI walk against the configured router. Used to:
//   - sanity-check credentials before kicking off a full autodiscover
//   - surface "found N LAN hosts, WG already configured" preview text
// Every failure mode returns 200 + `{ok: false, error}` so the caller
// handles all cases in one branch.
// BE: services/plugins/equipment_discovery/api/zone_router.py:zone_router_probe
// Spec: docs/superpowers/specs/2026-05-31-router-autodiscovery.md §4.3.

export type RouterProbeHostKind = 'camera' | 'nvr' | 'likely_other' | 'unknown';

/**
 * Operator-set host designation (2026-06-02 redesign Group A endpoint).
 * Overrides the hostname-heuristic ``kind`` when set. Persisted across
 * probes via a server-side host-designation table keyed on (zone, mac).
 */
export type RouterHostDesignation = 'camera' | 'nvr' | 'router';

export interface RouterProbeLanHost {
  ip: string;
  mac: string;
  host_name: string | null;
  interface: string | null;
  via: string;
  active: boolean;
  // Heuristic hostname-based classification populated by the BE.
  // See keenetic_rci.classify_lan_host for the rules.
  kind: RouterProbeHostKind;
  // 2026-06-02 redesign annotations — populated by Group A when shipped,
  // absent otherwise (FE treats `undefined` as "not yet deployed" and
  // falls back to the kind-only flow).
  imported_as_camera_id?: number | null;
  imported_as_camera_name?: string | null;
  imported_via_nvr_id?: number | null;
  /** Operator override stored on the BE; takes priority over `kind`. */
  designation?: RouterHostDesignation | null;
}

/**
 * One Keenetic port-forward rule, normalised onto underscored keys.
 *
 * `to_host` is whichever form the router stored — Keenetic accepts both
 * MACs and IPs. The FE joins forwards onto LAN hosts by comparing
 * `to_host` case-insensitively against the host's `ip` AND `mac`.
 */
export interface RouterProbeForward {
  interface: string | null;
  protocol: string | null;
  external_port: number | null;
  internal_port: number | null;
  to_host: string | null;
  comment: string;
  /** Device-side hash that uniquely identifies the rule. Delete pipes
   *  this back to the BE — see ``deleteRouterForward``. */
  index?: string | null;
}

export interface RouterProbeOk {
  ok: true;
  model: string | null;
  firmware_version: string | null;
  hostname: string | null;
  wireguard_component_installed: boolean;
  wan_interface: string | null;
  wg_state: 'configured' | 'absent' | 'wrong_scope' | string;
  wg_uplink_ip: string | null;
  lan_hosts_count: number;
  likely_cameras_count: number;
  likely_nvrs_count: number;
  lan_hosts: RouterProbeLanHost[];
  existing_forwards_count: number;
  // Added 2026-06-02 — Phase A.5 import-cameras flow. The FE pairs
  // each suggested-camera row with the matching forward(s) inline and
  // arms a checkbox for the ones with a working RTSP-554 forward.
  existing_forwards: RouterProbeForward[];
}

export interface RouterProbeError {
  ok: false;
  error: 'credentials_rejected' | 'unreachable' | 'enumerate_failed' | string;
  detail: string;
}

export type RouterProbeResponse = RouterProbeOk | RouterProbeError;

export const probeZoneRouter = (zoneId: number | string) =>
  axiosInstance.post<RouterProbeResponse>(`${url(zoneId)}probe/`);

// ---- Cached probe payload (2026-06-02 Router-card restructure) ----------
//
// Returns the last successful POST .../router/probe/ payload + the
// timestamp it was fetched. Lets the Router card render model /
// firmware / WG / host counts on mount without re-walking the router.
// Empty cache (never probed) → { ok: true, cached: false, payload: null }.

export type RouterProbeCacheResponse =
  | {
      ok: true;
      cached: true;
      fetched_at: string;
      payload: RouterProbeOk;
    }
  | {
      ok: true;
      cached: false;
      fetched_at: null;
      payload: null;
    };

export const getZoneRouterProbeCache = (zoneId: number | string) =>
  axiosInstance.get<RouterProbeCacheResponse>(`${url(zoneId)}probe/`);

// ---- Router import-cameras (autodiscovery Phase A.5) --------------------
//
// Turn router-side port-forwards into central-visible cameras. WG-routed
// only — the BE rejects with `wg_not_configured` if the router has no
// `wg_uplink_ip` (configure WireGuard first).
// BE: services/plugins/equipment_discovery/api/zone_router.py:zone_router_import_cameras

export interface RouterImportCameraInput {
  name: string;
  external_port: number;
  internal_port: number;
  lan_ip?: string | null;
  mac?: string | null;
  hostname?: string | null;
  /** Camera source type (matches the edit dialog's "Тип источника").
   *  Omitted → BE defaults to plain `rtsp`. Set to `ezviz` for the
   *  EZVIZ onboarding flow so the row carries the right vendor identity. */
  type?: string;
  /** Forwarded external port for the camera's HTTP/ISAPI admin. Omitted
   *  → BE defaults to 80. For EZVIZ this is the :8000 SDK/ISAPI forward
   *  (admin_ports maps ezviz → http:8000), since the camera is reached
   *  via the router's WG IP, not a bare :80. */
  api_port?: number;
}

export interface RouterImportCameraPayload {
  username: string;
  password: string;
  cameras: RouterImportCameraInput[];
  /**
   * Client-minted UUID4. The BE dedups by this key so a double-click /
   * network retry / submit-debounce miss can't create duplicate Camera
   * rows. Re-submitting the same key returns the prior result. FE mints
   * a fresh one when the confirm dialog opens; same value is reused for
   * any retries inside that dialog session.
   */
  idempotency_key?: string;
}

export interface RouterImportCameraResult {
  ok: boolean;
  created: Array<{
    camera_id: number;
    provider_id: number;
    name: string;
    ip_address: string;
    port: number;
  }>;
  skipped: Array<{ name: string; reason: string; camera_id?: number }>;
  errors: Array<{ name: string | null; detail: string }>;
}

export const importRouterCameras = (
  zoneId: number | string,
  payload: RouterImportCameraPayload,
) =>
  axiosInstance.post<RouterImportCameraResult>(
    `${url(zoneId)}import-cameras/`,
    payload,
  );

// ---- Router port-forward writes (autodiscovery Phase C) -----------------
//
// Add / remove a single router-side forwarding rule. Both POST and
// DELETE go to the same path, so we don't need to URL-encode an index;
// the router-internal hash that identifies the rule is matched by
// (interface, protocol, external_port, internal_port, to_host).
// BE: services/plugins/equipment_discovery/api/router_writes.py
//
// Every failure returns 200 + `{ok:false, error:<code>, detail}` so the
// caller dispatches on `error` rather than HTTP status. Typed errors:
//   - unsupported_router_model     vendor module isn't keenetic
//   - credentials_rejected         Keenetic auth failed
//   - unreachable                  transport-level (DNS/TCP/TLS)
//   - missing_field                payload missing required key
//   - bad_protocol                 protocol not in {tcp,udp}
//   - bad_external_port            external_port out of 1-65535
//   - bad_internal_port            internal_port out of 1-65535
//   - bad_comment                  comment >200 chars / multi-line
//   - external_port_reserved       external_port == 80 (would shadow admin)
//   - device_rejected              CLI parse error from the device
//   - readback_missing             POST didn't land in router state
//   - readback_still_present       DELETE didn't clear the rule

export interface RouterForwardWritePayload {
  interface: string;
  protocol: 'tcp' | 'udp';
  external_port: number;
  internal_port: number;
  to_host: string;
  comment?: string;
}

export interface RouterForwardWriteOk {
  ok: true;
  forward: RouterProbeForward & { index?: number };
}

export interface RouterForwardWriteError {
  ok: false;
  error: string;
  detail: string;
}

export type RouterForwardWriteResponse = RouterForwardWriteOk | RouterForwardWriteError;

export interface RouterForwardDeleteOk { ok: true }
export type RouterForwardDeleteResponse = RouterForwardDeleteOk | RouterForwardWriteError;

export const createRouterForward = (
  zoneId: number | string,
  payload: RouterForwardWritePayload,
) =>
  axiosInstance.post<RouterForwardWriteResponse>(
    `${url(zoneId)}forwards/`,
    payload,
  );

// DELETE with a body is unusual but matches the BE contract. We
// address rules by their device-side ``index`` hash — see the BE
// router_writes.py docstring for why (2026-06-02 restructure: some
// Keenetic firmwares strip ``to-host`` from the probe-side reads on
// WireGuard forwards, making the five-tuple unreconstructable).
//
// Read the index off the latest probe payload's
// ``existing_forwards[].index``.
export const deleteRouterForward = (
  zoneId: number | string,
  payload: { index: string },
) =>
  axiosInstance.delete<RouterForwardDeleteResponse>(
    `${url(zoneId)}forwards/`,
    { data: payload },
  );

// ---- 2026-06-02 Group A: auto-allocate port-forwards --------------------
//
// BE picks the next free external port for the given family (rtsp →
// 8554/9554/10554…; api → 8080/9080/10080…; other → operator-supplied
// internal_port + auto-picked external). Binds to the LAN host by MAC
// so the round-trip stops dropping `to_host`. Typed errors:
//   - no_reachable_path: router has no WG AND no public WAN IP.
//   - external_port_reserved: 80 in particular.
//   - endpoint_not_deployed: FE-synthetic for the staging window where
//     Group A hasn't landed yet.

// `bootstrap` (internal :8000, external band 8800/9800/10800…) is the
// Hik/EZVIZ SDK transport — forwarded first so the RTSP-enable SDK call
// can reach the camera before RTSP itself is turned on.
export type RouterAutoForwardFamily = 'rtsp' | 'api' | 'bootstrap' | 'other';

export interface RouterAutoForwardPayload {
  lan_host_mac: string;
  internal_port: number;
  family: RouterAutoForwardFamily;
}

export interface RouterAutoForwardOk {
  ok: true;
  external_port: number;
  forward: RouterProbeForward;
}

export interface RouterAutoForwardError {
  ok: false;
  error:
    | 'no_reachable_path'
    | 'external_port_reserved'
    | 'endpoint_not_deployed'
    | string;
  detail: string;
}

export type RouterAutoForwardResponse =
  | RouterAutoForwardOk
  | RouterAutoForwardError;

/**
 * Auto-allocate-and-create port forward. The BE picks the external port.
 *
 * Group A endpoint guard: when Group A hasn't shipped yet on the target
 * BE we get a 404. Synthesise a typed `endpoint_not_deployed` error so
 * the caller can fall back to the manual `createRouterForward` path
 * with a "BE not deployed" toast.
 */
export async function autoCreateRouterForward(
  zoneId: number | string,
  payload: RouterAutoForwardPayload,
): Promise<RouterAutoForwardResponse> {
  try {
    const r = await axiosInstance.post<RouterAutoForwardResponse>(
      `${url(zoneId)}forwards/auto/`,
      payload,
    );
    return r.data;
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) {
      return { ok: false, error: 'endpoint_not_deployed', detail: 'BE auto endpoint not deployed yet' };
    }
    throw err;
  }
}

// ---- 2026-06-02 Group A: connectivity checks ----------------------------
//
// Run a ping / TCP-open / RTSP-stream probe from the router-WG side. The
// FE wires this into the three-dots menu so an operator can sanity-check
// a candidate before importing.

export type RouterCheckKind = 'ping' | 'tcp_open' | 'rtsp_stream';

/**
 * 2026-06-03 contract update (coordinated with BE-B):
 *
 * The endpoint now accepts a discriminated `target` object instead of a
 * bare `lan_host`. Three target shapes are supported:
 *
 *   - `{kind: 'lan_host', value: '192.168.1.67'}`           — direct LAN ip
 *   - `{kind: 'forward_index', value: '<router rule index>'}` — check by forward
 *   - `{kind: 'external_endpoint', value: 'host:port'}`     — WAN-side endpoint
 *
 * To stay compatible with BE versions where the new shape isn't deployed
 * yet, callers go through `checkRouterConnectivity` which tries the new
 * payload first and on `bad_request: target_required` falls back to the
 * legacy `lan_host` shape transparently.
 */
export type RouterCheckTarget =
  | { kind: 'lan_host'; value: string }
  | { kind: 'forward_index'; value: string }
  | { kind: 'external_endpoint'; value: string };

export interface RouterCheckPayload {
  /** Discriminated target. Required for the new BE; the FE falls back
   *  to `lan_host` automatically when the BE rejects this shape. */
  target?: RouterCheckTarget;
  /** Legacy shape — kept as a transparent fallback when BE-B hasn't
   *  deployed yet. New callers should set `target` instead. */
  lan_host?: string;
  kind: RouterCheckKind;
  port?: number;
  path?: string;
}

export interface RouterCheckOk {
  ok: true;
  latency_ms?: number | null;
}

export interface RouterCheckError {
  ok: false;
  error: 'endpoint_not_deployed' | 'target_required' | string;
  detail?: string;
}

export type RouterCheckResponse = RouterCheckOk | RouterCheckError;

/**
 * Run a router-side connectivity probe.
 *
 * Sends the new `target`-shaped payload first; on a BE that hasn't
 * shipped that contract yet (`{ok:false, error:'target_required'}` OR
 * HTTP 400 on the new field), transparently retries with the legacy
 * `lan_host` shape so the operator never sees the contract shift.
 *
 * Caller must populate `target` for the new shape, AND/OR `lan_host`
 * for the fallback. When only `target` is supplied with kind=`lan_host`
 * the helper derives the fallback `lan_host` automatically.
 */
export async function checkRouterConnectivity(
  zoneId: number | string,
  payload: RouterCheckPayload,
): Promise<RouterCheckResponse> {
  // Derive a sensible `lan_host` fallback when only the new-shape target
  // was supplied — that's the most common call-site (router probe rows
  // pass a LAN ip), and it keeps the BE-B fallback purely mechanical.
  const fallbackLanHost =
    payload.lan_host ??
    (payload.target?.kind === 'lan_host' ? payload.target.value : undefined);

  const tryRequest = async (body: unknown) => {
    try {
      const r = await axiosInstance.post<RouterCheckResponse>(
        `${url(zoneId)}check/`,
        body,
      );
      return r.data;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        return { ok: false, error: 'endpoint_not_deployed' } as RouterCheckResponse;
      }
      // 400 on missing field — the BE didn't recognise `target` (older
      // pre-BE-B build). Treat it as `target_required` so the caller
      // routes through the fallback.
      if (status === 400) {
        const data = (err as { response?: { data?: unknown } })?.response?.data;
        const raw = typeof data === 'object' && data
          ? JSON.stringify(data).toLowerCase()
          : String(data ?? '').toLowerCase();
        if (raw.includes('target') || raw.includes('lan_host')) {
          return { ok: false, error: 'target_required' } as RouterCheckResponse;
        }
      }
      throw err;
    }
  };

  // Build the primary (new-shape) body. Keep `lan_host` alongside when
  // we already have it — that lets the BE log both representations while
  // it's straddling the rollout.
  if (payload.target) {
    const primary: Record<string, unknown> = {
      target: payload.target,
      kind: payload.kind,
    };
    if (payload.port !== undefined) primary.port = payload.port;
    if (payload.path !== undefined) primary.path = payload.path;
    const r = await tryRequest(primary);
    if (r.ok || r.error !== 'target_required') return r;
    // Fall back to legacy lan_host shape.
    if (!fallbackLanHost) return r;
    const legacy: Record<string, unknown> = {
      lan_host: fallbackLanHost,
      kind: payload.kind,
    };
    if (payload.port !== undefined) legacy.port = payload.port;
    if (payload.path !== undefined) legacy.path = payload.path;
    return tryRequest(legacy);
  }

  // No new-shape target — caller used the legacy contract directly.
  const legacy: Record<string, unknown> = {
    kind: payload.kind,
  };
  if (payload.lan_host !== undefined) legacy.lan_host = payload.lan_host;
  if (payload.port !== undefined) legacy.port = payload.port;
  if (payload.path !== undefined) legacy.path = payload.path;
  return tryRequest(legacy);
}

// ---- 2026-06-02 Group A: designate host ---------------------------------
//
// Operator override for an unrecognised LAN host's kind. Persisted in
// equipment_discovery_host_designation (zone, mac). Subsequent probes
// surface `designation` on the corresponding host so the FE rendering
// reflects the override.

export interface RouterDesignatePayload {
  lan_host_ip: string;
  lan_host_mac: string;
  designation: RouterHostDesignation;
  suggested_name: string;
}

export interface RouterDesignateOk {
  ok: true;
}

export interface RouterDesignateError {
  ok: false;
  error: 'endpoint_not_deployed' | string;
  detail?: string;
}

export type RouterDesignateResponse =
  | RouterDesignateOk
  | RouterDesignateError;

export async function designateRouterHost(
  zoneId: number | string,
  payload: RouterDesignatePayload,
): Promise<RouterDesignateResponse> {
  try {
    const r = await axiosInstance.post<RouterDesignateResponse>(
      `${url(zoneId)}designate-host/`,
      payload,
    );
    return r.data;
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) {
      return { ok: false, error: 'endpoint_not_deployed' };
    }
    throw err;
  }
}

/**
 * Clear an operator-set designation. After a successful DELETE the host
 * row's `designation` field falls back to `null` on the next probe and
 * the BE's hostname heuristic owns the classification again.
 *
 * Used by the three-dots menu's "Сбросить" item (finding #14) — operators
 * need a way to undo a misclick that previously had no recovery short of
 * editing the BE database directly.
 */
export async function clearRouterHostDesignation(
  zoneId: number | string,
  payload: { lan_host_mac: string },
): Promise<RouterDesignateResponse> {
  try {
    const r = await axiosInstance.delete<RouterDesignateResponse>(
      `${url(zoneId)}designate-host/`,
      { data: payload },
    );
    return r.data;
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) {
      return { ok: false, error: 'endpoint_not_deployed' };
    }
    throw err;
  }
}

// ---- 2026-06-05 EZVIZ RTSP bootstrap (Hik SDK over :8000) ---------------
//
// EZVIZ cameras ship with RTSP disabled — the only open door is the Hik
// binary SDK on :8000. This flips the `servicesSwitch` flags so RTSP
// turns on, after which the camera streams like any Hik-OEM RTSP source.
// BE: services/plugins/equipment_discovery/api/zone_router.py
//     :zone_router_ezviz_bootstrap
//
// `external_port` is the router forward that maps to the camera's :8000.
// `verification_code` is the code on the device label (also the RTSP
// password for EZVIZ). Every failure returns 200 + {ok:false, error}.

export interface RouterEzvizBootstrapPayload {
  external_port: number;
  username?: string;
  verification_code: string;
}

export interface RouterEzvizBootstrapOk {
  ok: true;
  result?: unknown;
}

export interface RouterEzvizBootstrapError {
  ok: false;
  error:
    | 'wg_not_configured'
    | 'bad_request'
    | 'already_running'
    | 'bootstrap_failed'
    | 'sdk_unavailable'
    | 'endpoint_not_deployed'
    | string;
  detail?: string;
}

export type RouterEzvizBootstrapResponse =
  | RouterEzvizBootstrapOk
  | RouterEzvizBootstrapError;

export async function ezvizBootstrap(
  zoneId: number | string,
  payload: RouterEzvizBootstrapPayload,
): Promise<RouterEzvizBootstrapResponse> {
  try {
    const r = await axiosInstance.post<RouterEzvizBootstrapResponse>(
      `${url(zoneId)}ezviz-bootstrap/`,
      payload,
    );
    return r.data;
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) {
      return { ok: false, error: 'endpoint_not_deployed', detail: 'BE ezviz-bootstrap endpoint not deployed yet' };
    }
    throw err;
  }
}
